import axios from 'axios'
import store from '@/store'

const API_BASE_URL = 'https://clubtest.intrare.app'

function buildDateTime (date, hour) {
  if (!date) return ''
  if (!hour) return `${date}T00:00:00`
  return `${date}T${hour}:00`
}

function toArray (payload) {
  if (Array.isArray(payload)) return payload
  if (payload && Array.isArray(payload.results)) return payload.results
  if (payload && Array.isArray(payload.data)) return payload.data
  return []
}

function getCourseName (course, startHole) {
  if (course && typeof course === 'object') {
    return course.name || course.course_name || course.label || `Hoyo ${startHole || ''}`.trim()
  }

  if (typeof course === 'string' && course) {
    return course
  }

  if (typeof startHole === 'number' || typeof startHole === 'string') {
    return `Hoyo ${startHole}`
  }

  return 'Campo'
}

function getScoreLabel (scoreVsPar) {
  if (scoreVsPar <= -3) return 'Albatross'
  if (scoreVsPar === -2) return 'Eagle'
  if (scoreVsPar === -1) return 'Birdie'
  if (scoreVsPar === 0) return 'Par'
  if (scoreVsPar === 1) return 'Bogey'
  if (scoreVsPar === 2) return 'Doble Bogey'
  if (scoreVsPar >= 3) return 'Triple Bogey'
  return '--'
}

function normalizeHoles (holes) {
  if (!Array.isArray(holes)) return []

  return holes.map(hole => ({
    hole_number: hole.hole_number || 0,
    par: hole.par || 0,
    distance: hole.distance || 0,
    handicap_index: hole.handicap_index || 0,
    strokes: hole.strokes || 0,
    score_vs_par: typeof hole.score_vs_par === 'number' ? hole.score_vs_par : 0,
    score_label: hole.score_label || getScoreLabel(typeof hole.score_vs_par === 'number' ? hole.score_vs_par : 0)
  }))
}

function normalizeNine (nine) {
  const safeNine = nine || {}
  return {
    holes: normalizeHoles(safeNine.holes),
    par_total: safeNine.par_total || 0,
    strokes_total: safeNine.strokes_total || 0,
    score_vs_par: typeof safeNine.score_vs_par === 'number' ? safeNine.score_vs_par : 0
  }
}

function toNumberOrDefault (value, fallback = 0) {
  if (typeof value === 'number') return isNaN(value) ? fallback : value
  const parsed = parseFloat(value)
  return isNaN(parsed) ? fallback : parsed
}

export function normalizeScorecard (scorecard, meta = {}) {
  const firstNine = normalizeNine(scorecard.first_nine)
  const secondNine = normalizeNine(scorecard.second_nine)
  const summary = scorecard.summary || {}
  const summaryScoreVsPar = typeof summary.score_vs_par === 'number' ? summary.score_vs_par : 0

  return {
    id: scorecard.id || meta.id || '',
    reservation_id: meta.reservation_id || scorecard.reservation_id || '',
    player_name: scorecard.player_name || meta.player_name || meta.display_user || 'Sin nombre',
    player_key: scorecard.user_id || scorecard.user || scorecard.ccslp_user_id || meta.user_id || meta.ccslp_user_id || null,
    handicap: toNumberOrDefault(scorecard.handicap, 0),
    tee: scorecard.tee || null,
    tee_name: scorecard.tee && scorecard.tee.name ? scorecard.tee.name : '',
    course_name: scorecard.course_name || meta.course || 'Campo',
    date_played: scorecard.date_played || meta.date_played || buildDateTime(meta.date, meta.hour),
    first_nine: firstNine,
    second_nine: secondNine,
    summary: {
      total_par: summary.total_par || (firstNine.par_total + secondNine.par_total),
      total_strokes: summary.total_strokes || (firstNine.strokes_total + secondNine.strokes_total),
      net_score: summary.net_score || 0,
      score_vs_par: summaryScoreVsPar,
      score_label: summary.score_label || getScoreLabel(summaryScoreVsPar)
    },
    score_vs_par: summaryScoreVsPar,
    score_label: summary.score_label || getScoreLabel(summaryScoreVsPar)
  }
}

export function normalizeReservationMeta (reservation) {
  const invitedPeople = Array.isArray(reservation.invited_people) ? reservation.invited_people : []
  const reservationId = reservation.id || reservation.reservation_id || ''
  const courseName = getCourseName(reservation.course, reservation.start_hole)

  return {
    reservation_id: reservationId,
    id: reservationId,
    display_user: reservation.display_user || '',
    date: reservation.date || '',
    hour: reservation.hour || '',
    date_played: buildDateTime(reservation.date, reservation.hour),
    course: courseName,
    players_count: invitedPeople.length + (reservation.display_user ? 1 : 0),
    player_names: [reservation.display_user].concat(invitedPeople).filter(Boolean).join(', '),
    status: reservation.status
  }
}

export function normalizeReservationGroup (payload, meta = {}) {
  const players = Array.isArray(payload && payload.players)
    ? payload.players.map(player => normalizeScorecard(player, meta))
    : []

  return {
    reservation_id: payload && payload.reservation_id ? payload.reservation_id : meta.reservation_id,
    date: payload && payload.date ? payload.date : meta.date,
    date_played: players[0] && players[0].date_played ? players[0].date_played : meta.date_played,
    course: payload && payload.course ? payload.course : meta.course,
    players_count: payload && payload.players_count ? payload.players_count : players.length,
    player_names: players.map(player => player.player_name).join(', '),
    display_user: meta.display_user || (players[0] ? players[0].player_name : ''),
    players
  }
}

export function apiCall (method, endpoint, language, data = null) {
  const config = {
    method,
    url: `${API_BASE_URL}${endpoint}`,
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': language || 'es',
      authorization: 'Token ' + store.getters.getToken
    }
  }

  if (data) {
    config.data = data
  }

  return axios(config)
}

export function getDateRange (daysBack = 90, daysForward = 1) {
  const today = new Date()
  const startDate = new Date(today)
  const endDate = new Date(today)

  startDate.setDate(startDate.getDate() - daysBack)
  endDate.setDate(endDate.getDate() + daysForward)

  return {
    start: startDate.toISOString().slice(0, 10),
    end: endDate.toISOString().slice(0, 10)
  }
}

export async function fetchReservationScorecardGroup (reservationId, language, meta = {}) {
  const response = await apiCall('get', `/golf-scores/by_reservation_scorecard/?reservation_id=${reservationId}`, language)
  return normalizeReservationGroup(response.data, meta)
}

export async function fetchUserScorecard (scoreId, language) {
  const response = await apiCall('get', `/golf-scores/${scoreId}/scorecard/`, language)
  return normalizeScorecard(response.data)
}

export async function fetchBulkGolfScorecards (options = {}) {
  const language = options.language || 'es'
  const housingUnitId = options.housingUnitId
  const startDate = options.startDate
  const endDate = options.endDate

  // Construir URL con parámetros de query
  let endpoint = `/golf-scores/bulk_scorecards/?housing_unit=${housingUnitId}`
  if (startDate) endpoint += `&date_from=${startDate}`
  if (endDate) endpoint += `&date_to=${endDate}`

  const response = await apiCall('get', endpoint, language)
  const data = response.data

  // Procesar reservaciones desde la respuesta bulk
  const reservations = []
  const userScores = []
  const groupsByReservation = {}

  if (Array.isArray(data.reservations)) {
    data.reservations.forEach(reservationData => {
      const players = Array.isArray(reservationData.players)
        ? reservationData.players.map(player => normalizeScorecard(player, {
          reservation_id: reservationData.reservation_id,
          date_played: reservationData.date_played
        }))
        : []

      if (players.length === 0) return

      // Crear fila de reservación
      const reservationRow = {
        reservation_id: reservationData.reservation_id,
        date: reservationData.date,
        date_played: reservationData.date_played,
        course: reservationData.course_name || 'Campo',
        players_count: players.length,
        display_user: reservationData.display_user || players[0]?.player_name || '',
        player_names: players.map(p => p.player_name).join(', '),
        status: reservationData.status
      }

      groupsByReservation[reservationData.reservation_id] = players
      reservations.push(reservationRow)

      // Agregar scores individuales
      players.forEach(player => {
        userScores.push({
          ...player,
          reservation_id: reservationData.reservation_id,
          course_name: player.course_name || reservationData.course_name || 'Campo',
          date_played: player.date_played || reservationData.date_played
        })
      })
    })
  }

  // Ordenar por fecha
  reservations.sort((left, right) => new Date(right.date_played) - new Date(left.date_played))
  userScores.sort((left, right) => new Date(right.date_played) - new Date(left.date_played))

  return {
    reservations,
    userScores,
    groupsByReservation,
    summary: data.summary || {}
  }
}

export async function fetchGolfScorecardsDataset (options = {}) {
  const language = options.language || 'es'
  const housingUnitId = options.housingUnitId
  const range = {
    start: options.startDate,
    end: options.endDate
  }

  const reservationsResponse = await apiCall(
    'get',
    `/golf-reservations/by-date?start=${range.start}&end=${range.end}&housing_unit=${housingUnitId}`,
    language
  )

  const reservationCandidates = toArray(reservationsResponse.data)
    .map(normalizeReservationMeta)
    .filter(item => item.reservation_id)

  const settled = await Promise.allSettled(
    reservationCandidates.map(item => fetchReservationScorecardGroup(item.reservation_id, language, item))
  )

  const reservations = []
  const userScores = []
  const groupsByReservation = {}

  settled.forEach((result, index) => {
    if (result.status !== 'fulfilled') {
      return
    }

    const group = result.value
    if (!group.players.length) {
      return
    }

    const reservationMeta = reservationCandidates[index]
    const reservationRow = {
      reservation_id: group.reservation_id,
      date: group.date || reservationMeta.date,
      date_played: group.date_played || reservationMeta.date_played,
      course: group.course || reservationMeta.course,
      players_count: group.players_count || group.players.length,
      display_user: reservationMeta.display_user,
      player_names: group.player_names || reservationMeta.player_names,
      status: reservationMeta.status
    }

    groupsByReservation[group.reservation_id] = group.players
    reservations.push(reservationRow)

    group.players.forEach(player => {
      userScores.push({
        ...player,
        reservation_id: group.reservation_id,
        course_name: player.course_name || group.course || reservationMeta.course,
        date_played: player.date_played || group.date_played || reservationMeta.date_played
      })
    })
  })

  reservations.sort((left, right) => new Date(right.date_played) - new Date(left.date_played))
  userScores.sort((left, right) => new Date(right.date_played) - new Date(left.date_played))

  return {
    reservations,
    userScores,
    groupsByReservation
  }
}

function normalizePlayerNameKey (name) {
  return (name || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip accents so "José" === "jose"
    .replace(/\s+/g, ' ') // collapse repeated whitespace
}

export function aggregatePlayers (userScores) {
  const groups = {}

  userScores.forEach(score => {
    // Prefer a stable identity (resident/CCSLP id) when the backend provides one,
    // since grouping by name alone merges unrelated players who share a name.
    const key = score.player_key || normalizePlayerNameKey(score.player_name)
    if (!key) return

    const handicap = toNumberOrDefault(score.handicap, 0)
    const netScore = score.summary ? toNumberOrDefault(score.summary.net_score, null) : null

    if (!groups[key]) {
      groups[key] = {
        player_name: score.player_name,
        rounds_count: 0,
        total_handicap: 0,
        avg_handicap: 0,
        latest_handicap: handicap,
        best_net_score: netScore,
        best_score_vs_par: typeof score.score_vs_par === 'number' ? score.score_vs_par : Infinity,
        last_played: score.date_played,
        courses: {}
      }
    }

    const current = groups[key]
    current.rounds_count += 1
    current.total_handicap += handicap

    if (new Date(score.date_played) >= new Date(current.last_played)) {
      current.last_played = score.date_played
      current.latest_handicap = handicap
    }

    if (netScore !== null && (current.best_net_score === null || netScore < current.best_net_score)) {
      current.best_net_score = netScore
    }

    if (typeof score.score_vs_par === 'number' && score.score_vs_par < current.best_score_vs_par) {
      current.best_score_vs_par = score.score_vs_par
    }

    if (score.course_name) {
      current.courses[score.course_name] = true
    }
  })

  return Object.keys(groups)
    .map(key => {
      const player = groups[key]
      return {
        ...player,
        avg_handicap: player.rounds_count ? Number((player.total_handicap / player.rounds_count).toFixed(1)) : 0,
        courses: Object.keys(player.courses).join(', ')
      }
    })
    .sort((left, right) => new Date(right.last_played) - new Date(left.last_played))
}
